

import { FC, useEffect, useMemo, useState } from 'react'
import { SegmentType } from '../types'
import type { Feature, PluginSpace, PluginspaceFeature } from '../types'

type ModalFeaturesPluginspaceProps = {
    isOpen: boolean
    pluginspace: PluginSpace | null
    allFeatures: Feature[]
    getAllFeatures: () => Promise<Feature[]>
    getAllFeaturesOfPluginspace: (pluginspaceId: number) => Promise<PluginspaceFeature[]>
    createFeatureForPluginspace: (pluginspaceId: number, featureId: number) => Promise<boolean>
    removeFeatureForPluginspace: (pluginspaceId: number, featureId: number) => Promise<boolean>
    onClose: () => void
}

const normalizeSegment = (pluginspaceType?: string): SegmentType | undefined => {
    if (!pluginspaceType) return undefined
    const normalized = pluginspaceType.toLowerCase().trim()

    if (normalized === 'hotel') return SegmentType.hotel
    if (normalized === 'retail') return SegmentType.varejo

    if (normalized === 'fashion') return SegmentType.fashion
    if (normalized === 'general') return SegmentType.general
    if (normalized === 'food') return SegmentType.food
    if (normalized === 'varejo') return SegmentType.varejo
    if (normalized === 'hospitality') return SegmentType.hotel

    return undefined
}

export const ModalFeaturesPluginspace: FC<ModalFeaturesPluginspaceProps> = ({
    isOpen,
    pluginspace,
    allFeatures,
    getAllFeatures,
    getAllFeaturesOfPluginspace,
    createFeatureForPluginspace,
    removeFeatureForPluginspace,
    onClose,
}) => {
    const [loading, setLoading] = useState(false)
    const [savingFeatureId, setSavingFeatureId] = useState<number | null>(null)
    const [assignedIds, setAssignedIds] = useState<number[]>([])
    const [assignedItems, setAssignedItems] = useState<PluginspaceFeature[]>([])

    useEffect(() => {
        const load = async () => {
            if (!isOpen || !pluginspace?.id) return

            setLoading(true)
            try {
                if (!allFeatures.length) {
                    await getAllFeatures()
                }
                const currentAssigned = await getAllFeaturesOfPluginspace(pluginspace.id)
                setAssignedItems(currentAssigned)
                setAssignedIds(currentAssigned.map((item) => item.featureId))
            } finally {
                setLoading(false)
            }
        }

        load()
    }, [
        isOpen,
        pluginspace?.id,
        allFeatures.length,
        getAllFeatures,
        getAllFeaturesOfPluginspace,
    ])

    const displayFeatures = useMemo(() => {
        const segment = normalizeSegment(pluginspace?.type)
        const bySegment = allFeatures.filter(
            (feature) => feature.segmentType === segment || feature.segmentType === 'general'
        )

        const assignedFromRelation = assignedItems
            .map((item) => item.feature)
            .filter((feature) => !!feature)

        const mergedById = new Map<number, Feature>()

        for (const feature of bySegment) {
            mergedById.set(feature.id, feature)
        }

        for (const feature of assignedFromRelation) {
            mergedById.set(feature.id, feature)
        }

        return Array.from(mergedById.values()).sort((a, b) => a.name.localeCompare(b.name))
    }, [allFeatures, assignedItems, pluginspace?.type])

    const handleToggle = async (featureId: number) => {
        try {
            if (!pluginspace?.id) return

            setSavingFeatureId(featureId)
            const isAssigned = assignedIds.includes(featureId)

            const success = isAssigned
                ? await removeFeatureForPluginspace(pluginspace.id, featureId)
                : await createFeatureForPluginspace(pluginspace.id, featureId)

            if (success) {
                setAssignedIds((prev) =>
                    isAssigned ? prev.filter((id) => id !== featureId) : [...prev, featureId]
                )
            }

            setSavingFeatureId(null)
        } catch (error) {
            console.error('Erro ao alternar feature:', error)
            setSavingFeatureId(null)
        }
    }

    if (!isOpen) return null

    return (
        <div
            className='modal fade show d-block'
            style={{
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <div className='modal-dialog modal-lg'>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className='modal-title'>
                            Features da Pluginspace {pluginspace?.name ? `- ${pluginspace.name}` : ''}
                        </h5>
                        <button type='button' className='btn-close' onClick={onClose} aria-label='Fechar'></button>
                    </div>

                    <div className='modal-body'>
                        <div className='alert alert-light-primary py-3'>
                            <strong>Tipo:</strong> {pluginspace?.type || '-'}
                        </div>

                        {loading ? (
                            <div className='text-center py-6'>
                                <div className='spinner-border text-primary' role='status'>
                                    <span className='visually-hidden'>Carregando...</span>
                                </div>
                            </div>
                        ) : displayFeatures.length === 0 ? (
                            <div className='alert alert-warning mb-0'>
                                Nenhuma feature encontrada para o segmento deste tipo de pluginspace.
                            </div>
                        ) : (
                                    <div className='d-flex flex-column gap-3'>
                                        {displayFeatures.map((feature) => {
                                            const checked = assignedIds.includes(feature.id)
                                            const isSaving = savingFeatureId === feature.id

                                            return (
                                                <label key={feature.id} className='form-check form-check-custom form-check-solid'>
                                                    <input
                                                        className='form-check-input me-3'
                                                        type='checkbox'
                                                        checked={checked}
                                                        disabled={isSaving}
                                                        onChange={() => handleToggle(feature.id)}
                                                    />
                                                    <span className='form-check-label d-flex flex-column'>
                                                        <span className='fw-bold'>{feature.name}</span>
                                                        <small className='text-muted'>
                                                            Segmento: {feature.segmentType} {feature.description ? `• ${feature.description}` : ''}
                                                        </small>
                                                    </span>
                                                </label>
                                            )
                                        })}
                                    </div>
                                )}
                    </div>

                    <div className='modal-footer'>
                        <button type='button' className='btn btn-light' onClick={onClose}>
                            Fechar
						</button>
                    </div>
                </div>
            </div>
        </div>
    )
}